/* eslint-disable */
import React, { useEffect, useMemo } from "react";
import { connect } from "react-redux";
import { useLocation } from "react-router-dom";
import Header from "../../Header/Header";
import { history } from "../../../config/stores";
import { MapContainer, TileLayer, Marker, Popup, useMap } from "react-leaflet";
import MarkerClusterGroup from "react-leaflet-cluster";
import L from "leaflet";
import "leaflet/dist/leaflet.css";
import "./PropertiesMap.scss";
import locateIcon from "../../../assets/icons/locate-gray.svg";
import listActiveIcon from "../../../assets/icons/map/list-active.svg";
import listIcon from "../../../assets/icons/map/list.svg";
import mapIcon from "../../../assets/icons/map/map.svg";
import mapActiveIcon from "../../../assets/icons/map/map-active.svg";
import centroids from "./cityCentroids.json";
import { getPropertiesList, getStatisticsSuccess } from "../../../actions";

// Fix default marker icons for Leaflet (Webpack bundling)
import icon2x from "leaflet/dist/images/marker-icon-2x.png";
import icon from "leaflet/dist/images/marker-icon.png";
import shadow from "leaflet/dist/images/marker-shadow.png";
const DefaultIcon = L.icon({ iconUrl: icon, shadowUrl: shadow, iconRetinaUrl: icon2x, iconSize: [25, 41], iconAnchor: [12, 41] });
L.Marker.prototype.options.icon = DefaultIcon;

// Helper: city centroid lookup
const resolveCentroid = (countryCode, cityEN) => {
  if (!countryCode || !cityEN) return null;
  const cc = String(countryCode).toLowerCase();
  const cityKey = String(cityEN).toLowerCase();
  const ccMap = centroids[cc];
  if (!ccMap) return null;
  return ccMap[cityKey] || null;
};

const FitBounds = ({ points }) => {
  const map = useMap();
  useEffect(() => {
    if (!points || points.length === 0) return;
    const coords = points
      .map((p) => [p.jlat ?? p.lat, p.jlng ?? p.lng])
      .filter(([lat, lng]) => typeof lat === 'number' && typeof lng === 'number');
    if (coords.length === 0) return;
    const bounds = L.latLngBounds(coords);
    map.fitBounds(bounds.pad(0.2));
  }, [points, map]);
  return null;
};

const LocationControl = () => {
  const map = useMap();
  const locate = () => {
    map.locate({ setView: true, maxZoom: 12 });
  };
  return (
    <button className="map-ctrl map-ctrl_primary" onClick={locate} title="Go to my location">
      <img src={locateIcon} alt="Locate" width={20} height={20} />
    </button>
  );
};

const ZoomControls = () => {
  const map = useMap();
  return (
    <div className="map-ctrl-group">
      <button className="map-ctrl" onClick={() => map.zoomIn()} title="Zoom in">+</button>
      <button className="map-ctrl" onClick={() => map.zoomOut()} title="Zoom out">−</button>
    </div>
  );
};

const PropertiesMap = ({ propertiesList, getPropertiesList, getStatisticsSuccess }) => {
  const center = useMemo(() => [50.1109, 8.6821], []); // Frankfurt-ish center
  const location = useLocation();

  // Parse URL filters to keep map/list in sync (same as PropertiesList)
  const urlParams = new URLSearchParams(location.search);
  const countryFilter = decodeURIComponent(urlParams.get('country') || '');
  const cityFilter = decodeURIComponent(urlParams.get('city') || '');
  const propertyTypeFilter = decodeURIComponent(urlParams.get('propertyType') || '');
  const startDateFilter = urlParams.get('startDate');
  const endDateFilter = urlParams.get('endDate');

  useEffect(() => {
    // Load properties with a larger page size to visualize enough data on the map
    getStatisticsSuccess({ type: "propertiesList", loading: true });
    getPropertiesList({
      page: 1,
      limit: 1000,
      onSuccess: () => {},
      onError: () => {},
    });
  }, [getPropertiesList, getStatisticsSuccess]);

  // Small dot icon for single properties
  const smallDotIcon = useMemo(() => new L.DivIcon({
    html: '<div class="property-dot"></div>',
    className: '',
    iconSize: [12, 12],
    iconAnchor: [6, 6],
  }), []);

  // Deterministic jitter to spread points within each city
  const seededRandom = (seed) => {
    const x = Math.sin(seed) * 10000;
    return x - Math.floor(x);
  };

  const jitterCoords = (lat, lng, seedBase, maxMeters = 2000, minMeters = 250) => {
    const rRand = seededRandom(seedBase + 1);
    const tRand = seededRandom(seedBase + 2);
    const r = minMeters + rRand * (maxMeters - minMeters); // radius in meters
    const theta = tRand * Math.PI * 2; // angle
    const dx = r * Math.cos(theta); // meters east
    const dy = r * Math.sin(theta); // meters north
    const dLat = dy / 111320; // degrees
    const dLng = dx / (111320 * Math.cos(lat * Math.PI / 180)); // degrees adjusted by latitude
    return [lat + dLat, lng + dLng];
  };

  // Build marker set from real data using centroid lookups
  const jitteredProperties = useMemo(() => {
    const docs = propertiesList?.docs || [];
    // Apply basic client-side filtering to mirror list state
    const filtered = docs.filter((prop) => {
      if (!prop) return false;
      if (countryFilter) {
        const cc = String(prop.countryCode || '').toLowerCase();
        if (cc !== String(countryFilter).toLowerCase()) return false;
      }
      if (cityFilter) {
        if (String(prop.cityEN || '').toLowerCase() !== String(cityFilter).toLowerCase()) return false;
      }
      if (propertyTypeFilter) {
        if (String(prop.propertyType?.name || '').toLowerCase() !== String(propertyTypeFilter).toLowerCase()) return false;
      }
      if (startDateFilter && endDateFilter) {
        const dt = new Date(prop.createdAt);
        const s = new Date(startDateFilter);
        const e = new Date(endDateFilter);
        if (dt < s || dt > e) return false;
      }
      return true;
    });

    const out = [];
    for (const p of filtered) {
      const centroid = resolveCentroid(p.countryCode, p.cityEN);
      if (!centroid) continue; // skip if city unknown; could collect as "Unplaced" later
      const [lat, lng] = centroid;
      const seed = Math.abs((p._id || '').split('').reduce((a, c) => a + c.charCodeAt(0), 0));
      const [jlat, jlng] = jitterCoords(lat, lng, seed);
      out.push({
        id: p._id,
        jlat,
        jlng,
        city: p.cityEN,
        country: String(p.countryCode || '').toUpperCase(),
        name: p.propertyType?.name || 'Property',
        rent: p.rent,
        currency: p.currency,
        createdAt: p.createdAt,
        createdBy: p.createdBy,
        usersCount: p.usersCount,
      });
    }
    return out;
  }, [propertiesList, countryFilter, cityFilter, propertyTypeFilter, startDateFilter, endDateFilter]);

  return (
    <>
      <Header />
      <div className="main-container">
        <div className="inner-header-container">
          <div className="left-part">
            <div className="close-icon" onClick={() => history.push("/")}>×</div>
            <div className="inner-title">PROPERTIES IN THE APP</div>
          </div>
          <div className="right-part">
            <div className="view-switch" aria-label="Switch to list view">
              <button
                type="button"
                className="view-icon"
                title="Open list view"
                aria-label="Open list view"
                onClick={() => history.push(`/dashboard/properties/list${window.location.search || ''}`)}
              >
                <img src={listIcon} alt="List" width={22} height={22} />
              </button>
              <button
                type="button"
                className="view-icon active"
                title="Map view"
                aria-label="Map view (active)"
              >
                <img src={mapActiveIcon} alt="Map active" width={22} height={22} />
              </button>
            </div>
          </div>
        </div>
        <div className="body-container properties-map-container">
          <MapContainer center={center} zoom={5} scrollWheelZoom zoomControl={false} style={{ height: "70vh", width: "100%" }}>
            {/* Light gray or dark gray basemap: choose one by toggling URL */}
            <TileLayer
              attribution='&copy; <a href="https://www.openstreetmap.org/copyright">OSM</a> contributors & <a href="https://carto.com/attributions">CARTO</a>'
              url="https://{s}.basemaps.cartocdn.com/light_all/{z}/{x}/{y}{r}.png"
            />
            {/* For dark theme, use: https://{s}.basemaps.cartocdn.com/dark_all/{z}/{x}/{y}{r}.png */}

            <FitBounds points={jitteredProperties} />

            <MarkerClusterGroup
              chunkedLoading
              showCoverageOnHover={false}
              spiderfyOnMaxZoom
              iconCreateFunction={(cluster) => {
                const count = cluster.getChildCount();
                const html = `<div class=\"cluster-marker\"><span>${count}</span></div>`;
                return new L.DivIcon({ html, className: '', iconSize: [36, 36], iconAnchor: [18, 18] });
              }}
            >
              {jitteredProperties.map((p) => (
                <Marker key={p.id} position={[p.jlat, p.jlng]} icon={smallDotIcon}>
                  <Popup>
                    <div>
                      <div style={{ fontWeight: 600 }}>{p.name}</div>
                      <div>{p.city}, {p.country}</div>
                      {p.rent ? (
                        <div>Rent: {p.rent} {p.currency?.toUpperCase() || ''}</div>
                      ) : (
                        <div>Status: Owned</div>
                      )}
                      {p.createdBy?.fullName && <div>Owner: {p.createdBy.fullName}</div>}
                      {p.createdAt && (
                        <div>Added: {new Date(p.createdAt).toLocaleDateString('en-GB').replace(/\//g, '.')}</div>
                      )}
                    </div>
                  </Popup>
                </Marker>
              ))}
            </MarkerClusterGroup>

            {/* Custom controls bottom-right */}
            <div className="map-ctrls br">
              <LocationControl />
              <ZoomControls />
            </div>
          </MapContainer>
        </div>
      </div>
    </>
  );
};

const mapStateToProps = (state) => ({ propertiesList: state.dashboard.propertiesList });
const mapDispatchToProps = (dispatch) => ({
  getPropertiesList: (payload) => dispatch(getPropertiesList(payload)),
  getStatisticsSuccess: (payload) => dispatch(getStatisticsSuccess(payload)),
});

export default connect(mapStateToProps, mapDispatchToProps)(PropertiesMap);
